/* eslint-disable */
import React, { useState } from "react";
import { SmartImg } from "./utils";
import styles from "./portals/devRequests/devRequests.module.css";

const ICON_X = <svg viewBox="0 0 24 24"><path d="M18 6 6 18M6 6l12 12" /></svg>;
const ICON_SEND = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m22 2-7 20-4-9-9-4 20-7z" /><path d="M22 2 11 13" /></svg>;
const TYPE_COLORS = { DA: "#c2410c", DS: "#0369a1", DO: "#059669", CL: "#7c3aed" };
const PRIORITIES = [
  { k: "low", label: "Low", c: "#8e887b" },
  { k: "normal", label: "Normal", c: "#3f72a4" },
  { k: "high", label: "High", c: "#a86810" },
  { k: "urgent", label: "Urgent", c: "#c0503f" },
];

// ── DevRequestModal ────────────────────────────────────────────────────────────
// Creates a new dev request from a catalog item (admin only). Props kept for
// call-site compatibility (T/cardSt/btnP/btnS/inputSt/lblSt now unused — DNA).
export function DevRequestModal({ item, onClose, onSaved, supabase, showToast, T, cardSt, btnP, btnS, inputSt, lblSt, designers=[] }) {
  const [form, setForm] = useState({
    assigned_to: [],
    dev_type: "DA",
    notes: "",
    priority: "normal",
    deadline: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k,v) => setForm(p=>({...p,[k]:v}));

  const toggleDesigner = (name) => {
    set("assigned_to", form.assigned_to.includes(name)
      ? form.assigned_to.filter(n=>n!==name)
      : [...form.assigned_to, name]);
  };

  const handleSave = async () => {
    if (!form.assigned_to.length) { showToast("Select at least one designer", true); return; }
    setSaving(true);
    const record = {
      catalog_item_id: String(item.id),
      assigned_to: form.assigned_to,
      status: "pending",
      messages: [{
        _meta: {
          model: item.model||item.sku||"",
          brand: item.brand||"",
          sku: item.sku||"",
          images: item.images||[],
          dev_type: form.dev_type,
          priority: form.priority,
          notes: form.notes,
          deadline: form.deadline||"",
          material: item.material||"",
          shape1: item.shape1||"",
          shape2: item.shape2||"",
          gender: item.gender||"",
          frameCategory: item.frameCategory||"",
          collection: item.collection||"",
          color: item.color||"",
          origin: item.origin||"",
          location: item.location||"",
          assignedTo: item.assignedTo||"",
          notes_original: item.notes||"",
        }
      }],
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    const { data, error } = await supabase.from("dev_requests").insert(record).select().single();
    if (error) { showToast("Failed to create request: " + error.message, true); setSaving(false); return; }
    showToast(`Development request sent to ${form.assigned_to.join(", ")}`);
    if (onSaved) onSaved(data);
    onClose();
    setSaving(false);
  };

  return (
    <div className={`${styles.dna} ${styles.overlay}`} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className={styles.modal} style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.modalHead}>
          <div>
            <div className={styles.modalTitle}>Request development</div>
            <div className={styles.modalSub}>
              Based on <strong>{item.model}</strong>{item.brand ? ` · ${item.brand}` : ""}
            </div>
          </div>
          <button onClick={onClose} className={styles.closeBtn} aria-label="Close">{ICON_X}</button>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.formStack}>
            {/* Item preview */}
            {(item.images||[])[0] && (
              <div className={styles.previewCard} style={{ marginBottom: 0 }}>
                <SmartImg displayWidth={120} src={item.images[0]} alt="" className={styles.previewThumb} />
                <div>
                  <div className={styles.previewModel}>{item.model}</div>
                  <div className={styles.previewBrand}>{[item.brand, item.material, item.gender].filter(Boolean).join(" · ")}</div>
                </div>
              </div>
            )}

            {/* Dev type */}
            <div>
              <label className={styles.label}>Development type</label>
              <div className={styles.segRow}>
                {["DA","DS","DO","CL"].map(t=>{
                  const active = form.dev_type===t;
                  const c = TYPE_COLORS[t];
                  return (
                    <button key={t} onClick={()=>set("dev_type",t)} className={styles.segBtn}
                      style={active ? { borderColor:c, background:c+"14", color:c, fontWeight:700 } : undefined}>
                      {t}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Assign to */}
            <div>
              <label className={styles.label}>Assign to designer(s)</label>
              <div className={styles.pillRow}>
                {designers.map(name=>{
                  const active = form.assigned_to.includes(name);
                  return (
                    <button key={name} onClick={()=>toggleDesigner(name)}
                      className={`${styles.pill} ${active ? styles.pillActive : ''}`}>
                      {name}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Priority */}
            <div>
              <label className={styles.label}>Priority</label>
              <div className={styles.segRow}>
                {PRIORITIES.map(p=>{
                  const active=form.priority===p.k;
                  return (
                    <button key={p.k} onClick={()=>set("priority",p.k)} className={styles.segBtn}
                      style={active ? { borderColor:p.c, background:p.c+"14", color:p.c, fontWeight:700 } : undefined}>
                      {p.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Deadline */}
            <div>
              <label className={styles.label}>Deadline (optional)</label>
              <input type="date" value={form.deadline||""} onChange={e=>set("deadline",e.target.value)} className={styles.input}/>
            </div>

            {/* Notes */}
            <div>
              <label className={styles.label}>Briefing notes</label>
              <textarea value={form.notes} onChange={e=>set("notes",e.target.value)} rows={3}
                placeholder="What changes, references, or instructions for the designer…"
                className={styles.textarea}/>
            </div>
          </div>
        </div>

        <div className={styles.modalFoot}>
          <button onClick={onClose} className={styles.btnGhost}>Cancel</button>
          <button onClick={handleSave} disabled={saving} className={styles.btnPrimary}>
            {saving ? "Sending…" : <>{ICON_SEND} Send request</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// DevRequestsInbox removed — use DevRequestsPortal from portals/devRequests/DevRequestsPortal.js
